import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Box } from '@mui/material';

import PhoneFrame from '@/designs-dev/device/PhoneFrame';
import {
  InspectProvider,
  useInspect,
} from '@/designs-dev/inspect/InspectContext';
import DrillModeToggle from '@/designs-dev/inspect/DrillModeToggle';
import StageBar, { STAGE_BAR_ROOM } from '@/designs-dev/inspect/StageBar';
import InspectOverlay from '@/designs-dev/inspect/InspectOverlay';
import InspectPanel from '@/designs-dev/inspect/InspectPanel';
import LayersPanel from '@/designs-dev/inspect/LayersPanel';
import StateSelector from '@/designs-dev/inspect/StateSelector';
import GettingIn from '@/karehero-app/access/GettingIn';
import {
  GETTING_IN_SECTIONS,
  gettingInScenes,
} from '@/karehero-app/access/GettingIn.scenes';
import {
  introScenes,
  introSceneById,
} from '@/karehero-app/auth/PreSignUp.scenes';

// Getting in on its own stage: the intro through to You're in, and no further.
//
// Its own prototype rather than a link into the master, which is what it used to
// be: two menu rows opening the same screen read as the same prototype twice,
// and the walk carried on into Tell Us Once, which is a different project.
const Stage = () => {
  const { active, current, setActive, setCurrent, setHovered } = useInspect();
  const [searchParams, setSearchParams] = useSearchParams();

  const [sceneId, setSceneId] = useState(
    () => searchParams.get('scene') ?? gettingInScenes[0].id,
  );
  const scene =
    gettingInScenes.find((s) => s.id === sceneId) ?? gettingInScenes[0];

  // Which opening the intro shows, on its own pill. Same catalog the master
  // prototype's switch reads, so the two cannot offer different options.
  const [optionId, setOptionId] = useState(
    () => searchParams.get('option') ?? 'chat',
  );
  const option = introSceneById(
    introScenes.some((s) => s.id === optionId) ? optionId : 'chat',
  ).initial;

  // Both in the URL, so a screen and an opening are a shareable address.
  useEffect(() => {
    if (
      searchParams.get('scene') !== scene.id ||
      searchParams.get('option') !== optionId
    ) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('scene', scene.id);
          next.set('option', optionId);
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene.id, optionId]);

  const exitDrillMode = useCallback(() => {
    setActive(false);
    setCurrent(null);
    setHovered(null);
  }, [setActive, setCurrent, setHovered]);

  // Esc is staged: a first press clears a selected layer, a second leaves drill
  // mode. Matches the other stages.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (current) {
        setCurrent(null);
        setHovered(null);
      } else {
        exitDrillMode();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, current, exitDrillMode, setCurrent, setHovered]);

  const pick = (set: (id: string) => void) => (id: string) => {
    setCurrent(null);
    setHovered(null);
    set(id);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: active ? 'custom.charcoal' : 'custom.neutral.100',
        transition: 'background-color 400ms ease',
      }}
    >
      {/* The stage, with the controls floating over its foot. */}
      <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            pb: STAGE_BAR_ROOM,
            overflow: 'auto',
          }}
        >
          {/* Keyed on both, so picking a screen or an opening starts it clean
              rather than carrying the last walk into it. */}
          <PhoneFrame inspecting={active}>
            <GettingIn
              key={`${scene.id}-${optionId}`}
              initial={scene.initial}
              option={option}
            />
            <InspectOverlay />
          </PhoneFrame>
        </Box>

        {/* The controls, floating at the foot of the stage: how you are
            looking at it, which opening it starts on, and which screen is
            showing. */}
        <StageBar>
          <DrillModeToggle />
          <StateSelector
            scenes={introScenes}
            sceneId={optionId}
            onSceneChange={pick(setOptionId)}
          />
          <StateSelector
            scenes={gettingInScenes}
            sceneId={scene.id}
            onSceneChange={pick(setSceneId)}
            sections={GETTING_IN_SECTIONS}
          />
        </StageBar>
      </Box>

      <LayersPanel />
      <InspectPanel />
    </Box>
  );
};

const GettingInStage = () => (
  <InspectProvider>
    <Stage />
  </InspectProvider>
);

export default GettingInStage;
